'use client'

import Link from 'next/link'

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaGem } from 'react-icons/fa'
import { useAuth } from '../contexts/AuthContext'
import * as gemsApi from '../lib/api/gems.ts'

/**
 * Secondary "claim your daily Gems" CTA for the gem wall.
 *
 * Why this exists: users who hit the gem wall convert ~37% better when they've
 * claimed a daily reward, but only ~3.5% of wall-hitters ever visit /earn-gems —
 * it's a nav-drawer destination nobody navigates to. Surfacing it at the moment
 * of friction is what closes that gap.
 *
 * Renders nothing unless there is actually an unclaimed reward waiting, so it
 * can never show a dead end. Upgrade stays the primary action wherever this is
 * used; this is deliberately the quieter option.
 */
const DailyGemsCta = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [reward, setReward] = useState(null)

  useEffect(() => {
    // Mounts only when a wall modal actually renders, so this costs nothing on
    // the generate hot path.
    let cancelled = false

    const load = async () => {
      if (!user?.uid) return
      try {
        const idToken = await user.getIdToken()
        const response = await gemsApi.earnGemsStatus(idToken, { json: false })
        if (!response.ok) return
        const data = await response.json()
        if (cancelled) return
        if (data?.daily && !data.daily.claimed_today) {
          setReward(data.daily.reward)
        }
      } catch {
        // Silent: this is a bonus affordance on top of an error modal that
        // already has its own message. Failing to show it must not add noise.
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [user])

  if (!reward) return null

  return (
    <Link
      href="/earn-gems"

      className="mt-4 inline-flex items-center justify-center gap-2 rounded-full border border-[var(--main-bg-color)] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[var(--main-bg-color)]/20"
    >
      <FaGem className="text-xs" />
      {t('claimDailyGemsCta')} · {reward}
    </Link>
  )
}

export default DailyGemsCta
